import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const AuthPage = () => {
  const navigate = useNavigate();
  const { 
    isAuthenticated, 
    sendLoginOTP, 
    sendSignupOTP, 
    verifyOTP, 
    resendOTP,
    otpLoading, 
    error, 
    setError 
  } = useAuth();

  const [mode, setMode] = useState('login'); // 'login' or 'signup'
  const [step, setStep] = useState('mobile'); // 'mobile' or 'otp'
  const [formData, setFormData] = useState({
    mobile: '',
    name: '',
    otp: ''
  });
  const [timer, setTimer] = useState(0);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    let interval;
    if (timer > 0) {
      interval = setInterval(() => {
        setTimer(prev => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [timer]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (error) {
      setError(null);
    }
  };

  const formatMobile = (mobile) => {
    // Remove any non-digit characters
    const cleaned = mobile.replace(/\D/g, '');
    
    // Add +91 if not present
    if (cleaned.length === 10) {
      return `+91${cleaned}`;
    } else if (cleaned.startsWith('91') && cleaned.length === 12) {
      return `+${cleaned}`;
    }
    return cleaned.startsWith('+') ? cleaned : `+${cleaned}`;
  };

  const validateMobile = (mobile) => {
    const cleaned = mobile.replace(/\D/g, '');
    return cleaned.length === 10 || 
           (cleaned.startsWith('91') && cleaned.length === 12);
  };

  const handleSendOTP = async (e) => {
    e.preventDefault();
    
    if (!validateMobile(formData.mobile)) {
      setError('Please enter a valid 10-digit mobile number');
      return;
    }

    const formattedMobile = formatMobile(formData.mobile);
    
    try {
      let result;
      if (mode === 'login') {
        result = await sendLoginOTP(formattedMobile);
      } else {
        if (!formData.name.trim()) {
          setError('Please enter your name');
          return;
        }
        result = await sendSignupOTP(formattedMobile, formData.name.trim());
      }

      if (result.success) {
        setStep('otp');
        setTimer(30); // 30 second timer for resend
        setFormData(prev => ({ ...prev, mobile: formattedMobile }));
      }
    } catch (error) {
      console.error('Error sending OTP:', error);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    
    if (!formData.otp || formData.otp.length !== 4) {
      setError('Please enter a valid 4-digit OTP');
      return;
    }

    try {
      const result = await verifyOTP(formData.mobile, formData.otp, mode);
      
      if (result.success) {
        // Redirect to home page or intended destination
        navigate('/');
      }
    } catch (error) {
      console.error('Error verifying OTP:', error);
    }
  };

  const handleResendOTP = async () => {
    if (timer > 0) return;
    
    try {
      const result = await resendOTP(formData.mobile, mode);
      
      if (result.success) {
        setTimer(30);
        setFormData(prev => ({ ...prev, otp: '' }));
      }
    } catch (error) {
      console.error('Error resending OTP:', error);
    }
  };

  const switchMode = () => {
    setMode(mode === 'login' ? 'signup' : 'login');
    setStep('mobile');
    setFormData({ mobile: '', name: '', otp: '' });
    setError(null);
    setTimer(0);
  };

  const goBack = () => {
    setStep('mobile');
    setFormData(prev => ({ ...prev, otp: '' }));
    setError(null);
  };

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <h2>{mode === 'login' ? 'Welcome Back!' : 'Join Bunny Boba!'}</h2>
            <p>
              {step === 'mobile'
                ? mode === 'login' 
                  ? 'Enter your mobile number to login'
                  : 'Create your account to start ordering'
                : 'Enter the OTP sent to your mobile'
              }
            </p>
          </div>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          {step === 'mobile' ? (
            <form onSubmit={handleSendOTP} className="auth-form">
              {mode === 'signup' && (
                <div className="form-group">
                  <label htmlFor="name">Full Name</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Enter your full name"
                    required={mode === 'signup'}
                  />
                </div>
              )}
              
              <div className="form-group">
                <label htmlFor="mobile">Mobile Number</label>
                <input
                  type="tel"
                  id="mobile"
                  name="mobile"
                  value={formData.mobile}
                  onChange={handleInputChange}
                  placeholder="Enter your mobile number"
                  required
                />
                <small>We'll send you an OTP to verify your number</small>
              </div>

              <button 
                type="submit" 
                className="btn btn-primary btn-full"
                disabled={otpLoading}
              >
                {otpLoading ? 'Sending OTP...' : 'Send OTP'}
              </button>
            </form>
          ) : (
            <form onSubmit={handleVerifyOTP} className="auth-form">
              <div className="form-group">
                <label htmlFor="otp">Enter OTP</label>
                <input
                  type="text"
                  id="otp"
                  name="otp"
                  value={formData.otp}
                  onChange={handleInputChange}
                  placeholder="4-digit OTP"
                  maxLength="4"
                  required
                />
                <small>OTP sent to {formData.mobile}</small>
              </div>

              <button 
                type="submit" 
                className="btn btn-primary btn-full"
                disabled={otpLoading}
              >
                {otpLoading ? 'Verifying...' : 'Verify OTP'}
              </button>

              <div className="otp-actions">
                <button 
                  type="button" 
                  onClick={goBack}
                  className="btn btn-secondary btn-small"
                >
                  Change Number
                </button>
                
                <button
                  type="button"
                  onClick={handleResendOTP}
                  disabled={timer > 0 || otpLoading}
                  className="btn btn-secondary btn-small"
                >
                  {timer > 0 ? `Resend in ${timer}s` : 'Resend OTP'}
                </button>
              </div>
            </form>
          )}

          <div className="auth-footer">
            <p>
              {mode === 'login' ? "Don't have an account? " : "Already have an account? "}
              <button onClick={switchMode} className="link-button">
                {mode === 'login' ? 'Sign Up' : 'Login'}
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthPage; 